// src/components/CompoundGoalCalculator.js
// Calcolatore Obiettivi con Tassazione Italiana — A2 initiative
// Three fixed scenarios: Conto Deposito / BTP / ETF Globale
// Mode A: given monthly contribution → final amounts
// Mode B: given target amount → required monthly contribution (binary search)
// Advanced: editable gross return + annual cost per scenario; optional inflation adjustment
// Tax (revisione 29/09/2026 — prima era applicata solo al realizzo per tutti gli scenari):
//   - Conto deposito: 26% trattenuto sugli interessi quando maturano (nessun differimento)
//   - BTP a tasso fisso: 12,5% trattenuto sulle cedole quando pagate; acquisto alla pari e
//     detenzione a scadenza (nessuna plus/minusvalenza)
//   - ETF ad accumulazione: 26% sulla plusvalenza solo al realizzo
// Rendimenti costanti, senza volatilità: il confronto NON misura il rischio.
// Chart: Chart.js LineChart with target line as dataset (no annotation plugin needed)

import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { Link } from "react-router-dom";
import "./FIRECalculator.css";       // reuse shared palette: .fire-panel, .fire-field, .fire-input, etc.
import "./CompoundGoalCalculator.css"; // new classes: cgc-*
import { LangContext } from "../lang/LangProvider";
import { useAnalytics } from "../hooks/useAnalytics";

Chart.register(...registerables);

// ── i18n helper ─────────────────────────────────────────────────────────────
function L(lang, it, en) { return lang === "en" ? en : it; }

// ── Number formatter (Italian locale, no decimals) ──────────────────────────
function fmt(value, sym) {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(value) + "\u202f" + sym;
}

// ── Preset goal definitions ──────────────────────────────────────────────────
const PRESETS = [
  { id: "home",      emojiIt: "🏠", emojiEn: "🏠", labelIt: "Casa",       labelEn: "Home",       target: 60000, years: 7  },
  { id: "education", emojiIt: "🎓", emojiEn: "🎓", labelIt: "Università", labelEn: "Education",  target: 50000, years: 15 },
  { id: "wedding",   emojiIt: "💍", emojiEn: "💍", labelIt: "Matrimonio", labelEn: "Wedding",    target: 25000, years: 4  },
  { id: "car",       emojiIt: "🚗", emojiEn: "🚗", labelIt: "Auto",       labelEn: "Car",        target: 30000, years: 5  },
  { id: "emergency", emojiIt: "🛡️", emojiEn: "🛡️", labelIt: "Emergenza",  labelEn: "Emergency",  target: 15000, years: 2  },
  { id: "custom",    emojiIt: "✏️", emojiEn: "✏️", labelIt: "Custom",     labelEn: "Custom",     target: null,  years: null },
];

// ── Default scenario parameters ──────────────────────────────────────────────
// grossReturn: ipotesi predefinita modificabile (%), NON media storica verificata
// cost: total annual drag — imposta di bollo + TER + commissions (%)
// taxRate: Italian regime amministrato (normative, not editable by user)
// taxTiming: "annual" = ritenuta su interessi/cedole quando maturano; "realization" = al realizzo
// color: chart line color
const DEFAULT_SCENARIOS = {
  deposit: { grossReturn: 3.0,  cost: 0.10, taxRate: 26,   taxTiming: "annual",      color: "#ef4444" },
  btp:     { grossReturn: 4.2,  cost: 0.25, taxRate: 12.5, taxTiming: "annual",      color: "#f59e0b" },
  etf:     { grossReturn: 7.0,  cost: 0.40, taxRate: 26,   taxTiming: "realization", color: "#1976d2" },
};

// ── Core math: monthly compounding ───────────────────────────────────────────
// Ogni mese: interessi lordi = saldo × grossReturn/12; costi = saldo × cost/12.
// "annual": l'imposta sugli interessi è trattenuta subito (niente capitalizzazione dell'imposta).
// "realization": l'imposta è applicata una sola volta sulla plusvalenza finale.
// Returns: { finalGross, finalNet, totalTax, totalInvested, gainGross, path }
// finalGross = capitale finale al lordo delle imposte (per "annual": saldo + imposte trattenute)
// path = saldo effettivo a fine di ogni anno (per "realization": prima dell'imposta finale)
function calcFinalAmount(initial, monthly, years, grossReturn, cost, taxRate, taxTiming = "realization") {
  const gM     = grossReturn / 100 / 12;
  const cM     = cost / 100 / 12;
  const tR     = taxRate / 100;
  const months = Math.round(years * 12);
  let balance  = initial;
  let taxPaid  = 0;
  const path   = [];
  for (let i = 0; i < months; i++) {
    const base     = balance + monthly;
    const interest = base * gM;
    const costAmt  = base * cM;
    if (taxTiming === "annual") {
      const tax = Math.max(0, interest) * tR;
      taxPaid  += tax;
      balance   = base + interest - costAmt - tax;
    } else {
      balance   = base + interest - costAmt;
    }
    if ((i + 1) % 12 === 0) path.push(balance);
  }
  const totalInvested = initial + monthly * months;
  if (taxTiming === "annual") {
    const finalGross = balance + taxPaid;
    return { finalGross, finalNet: balance, totalTax: taxPaid, totalInvested, gainGross: Math.max(0, finalGross - totalInvested), path };
  }
  const gainGross = Math.max(0, balance - totalInvested);
  const tax       = gainGross * tR;
  return { finalGross: balance, finalNet: balance - tax, totalTax: tax, totalInvested, gainGross, path };
}

// ── Binary search: required monthly to hit a net target ─────────────────────
// Returns: monthly contribution (integer €) or null if unreachable at €50k/mo
function calcRequiredMonthly(initial, target, years, grossReturn, cost, taxRate, taxTiming) {
  // Already met by initial capital alone
  const atZero = calcFinalAmount(initial, 0, years, grossReturn, cost, taxRate, taxTiming);
  if (atZero.finalNet >= target) return 0;

  // Check if even €50k/month is enough
  const atMax = calcFinalAmount(initial, 50000, years, grossReturn, cost, taxRate, taxTiming);
  if (atMax.finalNet < target) return null; // unreachable within this horizon

  // Binary search with €0.50 precision, max 60 iterations
  let lo = 0, hi = 50000;
  for (let iter = 0; iter < 60; iter++) {
    const mid    = (lo + hi) / 2;
    const result = calcFinalAmount(initial, mid, years, grossReturn, cost, taxRate, taxTiming);
    if (result.finalNet >= target) hi = mid;
    else                           lo = mid;
    if (hi - lo < 0.5) break;
  }
  return Math.ceil((lo + hi) / 2);
}

// ── Horizon bucket for analytics ─────────────────────────────────────────────
function horizonBucket(y) {
  return y <= 5 ? "≤5y" : y <= 10 ? "6-10y" : y <= 20 ? "11-20y" : ">20y";
}

// ── Main component ─────────────────────────────────────────────────────────
export default function CompoundGoalCalculator() {
  const { lang } = useContext(LangContext);
  const {
    trackToolStart,
    trackTimeOnTool,
    trackCompoundModeSwitched,
    trackCompoundPresetSelected,
    trackCompoundCalculation,
    trackCompoundAdvancedOpened,
  } = useAnalytics();

  // Track tool open + time-on-tool at unmount
  const startRef = useRef(Date.now());
  useEffect(() => {
    trackToolStart("compound_goal_calculator", lang);
    startRef.current = Date.now();
    return () => {
      const sec = Math.round((Date.now() - startRef.current) / 1000);
      trackTimeOnTool("compound_goal_calculator", sec, lang);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── State ────────────────────────────────────────────────────────────────
  const [mode,           setMode          ] = useState("B");   // "A" | "B"
  const [activePreset,   setActivePreset  ] = useState("home");
  const [initialCapital, setInitialCapital] = useState(5000);
  const [targetAmount,   setTargetAmount  ] = useState(60000);
  const [monthlyContrib, setMonthlyContrib] = useState(400);
  const [horizonYears,   setHorizonYears  ] = useState(7);
  const [scenarios,      setScenarios     ] = useState(DEFAULT_SCENARIOS);
  const [showAdvanced,   setShowAdvanced  ] = useState(false);
  const [inflationOn,    setInflationOn   ] = useState(false);
  const [inflationRate,  setInflationRate ] = useState(2.5);

  // ── Preset handler ───────────────────────────────────────────────────────
  const firstPresetRef = useRef(true);
  const handlePreset = (preset) => {
    if (!firstPresetRef.current) trackCompoundPresetSelected(preset.id, lang);
    firstPresetRef.current = false;
    setActivePreset(preset.id);
    if (preset.id !== "custom" && preset.target !== null) {
      setTargetAmount(preset.target);
      setHorizonYears(preset.years);
    }
  };

  // ── Mode switch handler ──────────────────────────────────────────────────
  const handleModeSwitch = (newMode) => {
    if (newMode !== mode) {
      trackCompoundModeSwitched(mode, newMode, lang);
      setMode(newMode);
    }
  };

  // ── Advanced toggle handler ──────────────────────────────────────────────
  const handleAdvancedToggle = () => {
    if (!showAdvanced) trackCompoundAdvancedOpened(lang);
    setShowAdvanced(prev => !prev);
  };

  // ── Update a single scenario field ──────────────────────────────────────
  const updateScenario = (id, field, value) => {
    setScenarios(prev => ({ ...prev, [id]: { ...prev[id], [field]: +value } }));
  };

  // ── Results (all 3 scenarios) ────────────────────────────────────────────
  const results = useMemo(() => {
    const out = {};
    for (const [id, sc] of Object.entries(scenarios)) {
      let monthly;
      let unreachable = false;
      if (mode === "A") {
        monthly = monthlyContrib;
      } else {
        const req = calcRequiredMonthly(initialCapital, targetAmount, horizonYears, sc.grossReturn, sc.cost, sc.taxRate, sc.taxTiming);
        if (req === null) { unreachable = true; monthly = 0; }
        else               monthly = req;
      }
      if (unreachable) {
        out[id] = { finalGross: 0, finalNet: 0, totalTax: 0, totalInvested: initialCapital, gainGross: 0, monthlyRequired: null, unreachable: true };
      } else {
        const r = calcFinalAmount(initialCapital, monthly, horizonYears, sc.grossReturn, sc.cost, sc.taxRate, sc.taxTiming);
        out[id] = { ...r, monthlyRequired: monthly, unreachable: false };
        if (inflationOn && horizonYears > 0) {
          const deflator = Math.pow(1 + inflationRate / 100, -horizonYears);
          out[id].realValue = r.finalNet * deflator;
        }
      }
    }
    return out;
  }, [mode, initialCapital, targetAmount, monthlyContrib, horizonYears, scenarios, inflationOn, inflationRate]);

  // ── Debounced analytics: compound_calculation ────────────────────────────
  const firstCalcRef = useRef(true);
  useEffect(() => {
    if (firstCalcRef.current) { firstCalcRef.current = false; return; }
    const timer = setTimeout(() => {
      trackCompoundCalculation(mode, activePreset, horizonBucket(horizonYears), lang);
    }, 1500);
    return () => clearTimeout(timer);
  }, [mode, initialCapital, targetAmount, monthlyContrib, horizonYears, activePreset]); // eslint-disable-line

  // ── Chart data (gross accumulation paths + optional target line) ─────────
  const chartData = useMemo(() => {
    const labels = Array.from({ length: horizonYears }, (_, i) =>
      `${L(lang, "Anno", "Year")} ${i + 1}`
    );
    const datasets = Object.entries(scenarios).map(([id, sc]) => {
      const monthly = mode === "A"
        ? monthlyContrib
        : (results[id]?.monthlyRequired ?? 0);
      return {
        label: id === "deposit"
          ? L(lang, "Conto Deposito", "Deposit Account")
          : id === "btp" ? L(lang, "BTP tasso fisso", "Fixed-rate BTP") : L(lang, "ETF azionario globale", "Global equity ETF"),
        data:            calcFinalAmount(initialCapital, monthly, horizonYears, sc.grossReturn, sc.cost, sc.taxRate, sc.taxTiming).path,
        borderColor:     sc.color,
        backgroundColor: sc.color + "18",
        fill:            false,
        tension:         0.35,
        pointRadius:     0,
        borderWidth:     id === "etf" ? 2.5 : 1.8,
      };
    });

    // Add dashed target line in Mode B
    if (mode === "B" && targetAmount > 0) {
      datasets.push({
        label:       L(lang, "Obiettivo", "Target"),
        data:        Array(horizonYears).fill(targetAmount),
        borderColor: "rgba(100,100,100,0.55)",
        borderDash:  [8, 4],
        fill:        false,
        pointRadius: 0,
        borderWidth: 1.5,
      });
    }
    return { labels, datasets };
  }, [scenarios, mode, initialCapital, monthlyContrib, horizonYears, results, targetAmount, lang]);

  // ── Shock card (best vs worst comparison) ───────────────────────────────
  const shockCard = useMemo(() => {
    const items = Object.entries(results)
      .filter(([, r]) => !r.unreachable)
      .map(([id, r]) => ({ id, net: r.finalNet, monthly: r.monthlyRequired }));
    if (items.length < 2) return null;
    // Revisione 29/09/2026: il testo nomina gli scenari effettivamente migliore/peggiore
    // (prima diceva sempre "ETF invece di Conto Deposito", anche con parametri modificati).
    if (mode === "A") {
      const sorted = [...items].sort((a, b) => a.net - b.net);
      const diff   = sorted[sorted.length - 1].net - sorted[0].net;
      return diff > 0 ? { diff, mode: "A", hi: sorted[sorted.length - 1].id, lo: sorted[0].id } : null;
    } else {
      const withMonthly = items.filter(i => i.monthly !== null && i.monthly > 0);
      if (withMonthly.length < 2) return null;
      const sorted = [...withMonthly].sort((a, b) => b.monthly - a.monthly);
      const diff   = sorted[0].monthly - sorted[sorted.length - 1].monthly;
      return diff > 0 ? { diff, mode: "B", hi: sorted[sorted.length - 1].id, lo: sorted[0].id } : null;
    }
  }, [results, mode]);

  const sym = "€";

  // KB article slug per language (created as companion article)
  const articlePath = L(lang,
    "/it/kb/interesse-composto-tassazione-italiana",
    "/en/kb/compound-interest-italian-taxation-guide"
  );

  // Label helpers
  // "BTP Italia" era fuorviante: è la famiglia di BTP indicizzati all'inflazione, mentre qui
  // lo scenario è un titolo di Stato a tasso fisso con cedola costante.
  const scenarioLabel = (id) =>
    id === "deposit" ? L(lang, "Conto Deposito", "Deposit Account")
    : id === "btp" ? L(lang, "BTP tasso fisso", "Fixed-rate BTP") : L(lang, "ETF azionario globale", "Global equity ETF");

  const costHint = (id) =>
    id === "deposit" ? L(lang, "Bollo: 34,20 €/anno fissi sopra 5.000 € di giacenza media sui conti liberi (≈0,10% su circa 34.000 €); 0,20% annuo sulle somme vincolate", "Stamp duty: fixed €34.20/year above €5,000 average balance on demand accounts (≈0.10% on about €34,000); 0.20% a year on time deposits")
    : id === "btp"   ? L(lang, "Bollo 0,20% + commissioni compravendita", "Stamp duty 0.20% + trading commissions")
    :                  L(lang, "TER + bollo 0,20% + commissioni broker", "TER + stamp duty 0.20% + broker fees");

  const taxLabel = (sc, id) => {
    const r = lang === "en" ? String(sc.taxRate) : String(sc.taxRate).replace(".", ",");
    return id === "deposit" ? L(lang, `Imposta ${r}% sugli interessi, ogni anno`, `Tax ${r}% on interest, every year`)
      : id === "btp"        ? L(lang, `Imposta ${r}% sulle cedole, ogni anno`, `Tax ${r}% on coupons, every year`)
      :                       L(lang, `Imposta ${r}% sulla plusvalenza, al realizzo`, `Tax ${r}% on the gain, at sale`);
  };

  return (
    <div className="cgc-calc">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="fire-header">
        <h2 className="fire-title">
          {L(lang, "Calcolatore Obiettivi", "Goal Planner")}
        </h2>
        <p className="fire-subtitle">
          {L(lang,
            "Imposta un obiettivo e confronta tre scenari (Conto Deposito, BTP, ETF) con ipotesi dichiarate di rendimento, costi e tassazione italiana semplificata.",
            "Set a goal and compare three scenarios (Deposit, BTP, ETF) with stated assumptions on returns, costs and simplified Italian taxation."
          )}
        </p>
      </div>

      {/* ── Mode toggle ─────────────────────────────────────────────────── */}
      <div className="cgc-mode-toggle">
        <button
          className={`cgc-mode-btn${mode === "A" ? " active" : ""}`}
          onClick={() => handleModeSwitch("A")}
        >
          {L(lang, "📥 Dato il mio versamento", "📥 Given my contribution")}
        </button>
        <button
          className={`cgc-mode-btn${mode === "B" ? " active" : ""}`}
          onClick={() => handleModeSwitch("B")}
        >
          {L(lang, "🎯 Dato il mio obiettivo", "🎯 Given my target")}
        </button>
      </div>
      <p className="cgc-mode-desc">
        {mode === "A"
          ? L(lang,
              "Inserisci quanto versi ogni mese e vedi quanto accumuli in 3 scenari.",
              "Enter how much you invest monthly and see how much you accumulate across 3 scenarios."
            )
          : L(lang,
              "Inserisci l'importo che vuoi raggiungere e scopri la rata mensile necessaria per scenario.",
              "Enter the amount you want to reach and find out the required monthly contribution per scenario."
            )}
      </p>

      {/* ── Preset chips ────────────────────────────────────────────────── */}
      <div className="cgc-presets">
        {PRESETS.map(p => (
          <button
            key={p.id}
            className={`cgc-preset-chip${activePreset === p.id ? " active" : ""}`}
            onClick={() => handlePreset(p)}
          >
            {p.emojiIt}&nbsp;{L(lang, p.labelIt, p.labelEn)}
          </button>
        ))}
      </div>

      {/* ── Input panel ─────────────────────────────────────────────────── */}
      <div className="fire-panel">
        <h3 className="fire-section-title">
          {L(lang, "Parametri", "Parameters")}
        </h3>

        <div className="fire-row">
          <div className="fire-field">
            <label className="fire-label">
              {L(lang, `Capitale iniziale (${sym})`, `Initial capital (${sym})`)}
            </label>
            <input
              className="fire-input"
              type="number" min={0}
              value={initialCapital}
              onChange={e => setInitialCapital(+e.target.value)}
            />
            <p className="fire-hint">
              {L(lang, "Risparmio già disponibile oggi", "Savings already available today")}
            </p>
          </div>
          <div className="fire-field">
            <label className="fire-label">
              {L(lang, "Orizzonte (anni)", "Horizon (years)")}
            </label>
            <input
              className="fire-input"
              type="number" min={1} max={40}
              value={horizonYears}
              onChange={e => setHorizonYears(Math.max(1, +e.target.value))}
            />
          </div>
        </div>

        {/* Dynamic field based on mode */}
        {mode === "B" ? (
          <div className="fire-field">
            <label className="fire-label">
              {L(lang, `Importo obiettivo netto (${sym})`, `Net target amount (${sym})`)}
            </label>
            <input
              className="fire-input"
              type="number" min={1000}
              value={targetAmount}
              onChange={e => setTargetAmount(+e.target.value)}
            />
            <p className="fire-hint">
              {L(lang,
                "Importo netto dopo le tasse che vuoi avere alla scadenza",
                "Net amount after taxes you want to have at maturity"
              )}
            </p>
          </div>
        ) : (
          <div className="fire-field">
            <label className="fire-label">
              {L(lang, `Versamento mensile (${sym})`, `Monthly contribution (${sym})`)}
            </label>
            <input
              className="fire-input"
              type="number" min={0}
              value={monthlyContrib}
              onChange={e => setMonthlyContrib(+e.target.value)}
            />
          </div>
        )}

        {/* Advanced accordion */}
        <button className="fire-advanced-toggle" onClick={handleAdvancedToggle}>
          <span>{L(lang, "Impostazioni avanzate", "Advanced settings")}</span>
          <span className="fire-chevron">{showAdvanced ? "▲" : "▼"}</span>
        </button>

        {showAdvanced && (
          <div className="fire-advanced">
            <div className="cgc-scenario-grid">
              {Object.entries(scenarios).map(([id, sc]) => (
                <div key={id} className="cgc-scenario-card" style={{ borderTop: `3px solid ${sc.color}` }}>
                  <div className="cgc-scenario-name" style={{ color: sc.color }}>
                    {scenarioLabel(id)}
                  </div>

                  <div className="fire-field">
                    <label className="fire-label">
                      {L(lang, "Rendimento lordo (%/anno)", "Gross return (%/yr)")}
                    </label>
                    <input
                      className="fire-input"
                      type="number" min={0} max={25} step={0.1}
                      value={sc.grossReturn}
                      onChange={e => updateScenario(id, "grossReturn", e.target.value)}
                    />
                  </div>

                  <div className="fire-field">
                    <label className="fire-label">
                      {L(lang, "Costi annui (%)", "Annual costs (%)")}
                    </label>
                    <input
                      className="fire-input"
                      type="number" min={0} max={5} step={0.05}
                      value={sc.cost}
                      onChange={e => updateScenario(id, "cost", e.target.value)}
                    />
                    <p className="fire-hint">{costHint(id)}</p>
                  </div>

                  <div className="fire-field">
                    <label className="fire-label">
                      {L(lang, "Aliquota fiscale (%)", "Tax rate (%)")}
                    </label>
                    <input
                      className="fire-input"
                      type="number"
                      value={sc.taxRate}
                      disabled
                      style={{ background: "#f3f4f6", color: "#9ca3af", cursor: "not-allowed" }}
                    />
                    <p className="fire-hint">
                      {L(lang, "Normativa italiana — non modificabile", "Italian law — not editable")}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Inflation toggle */}
            <div className="fire-field" style={{ marginTop: "1.25rem" }}>
              <label className="fire-label cgc-inflation-label">
                <input
                  type="checkbox"
                  checked={inflationOn}
                  onChange={e => setInflationOn(e.target.checked)}
                  style={{ marginRight: "0.5rem", accentColor: "#4f46e5" }}
                />
                {L(lang,
                  "Mostra valore reale (potere d'acquisto in €  di oggi)",
                  "Show real value (purchasing power in today's €)"
                )}
              </label>
              {inflationOn && (
                <>
                  <input
                    className="fire-input"
                    style={{ maxWidth: "160px", marginTop: "0.5rem" }}
                    type="number" min={0} max={15} step={0.1}
                    value={inflationRate}
                    onChange={e => setInflationRate(+e.target.value)}
                  />
                  <p className="fire-hint">
                    {L(lang, "Inflazione annua attesa (%)", "Expected annual inflation (%)")}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Results: comparison table ────────────────────────────────────── */}
      <div className="fire-results">
        <h3 className="fire-section-title">
          {L(lang, "Confronto Scenari", "Scenario Comparison")}
        </h3>

        <div className="cgc-table-wrap">
          {/* Header row */}
          <div className="cgc-table-row cgc-table-header-row">
            <div className="cgc-col cgc-col-label" />
            {Object.entries(scenarios).map(([id, sc]) => (
              <div key={id} className="cgc-col cgc-col-head" style={{ borderBottom: `3px solid ${sc.color}` }}>
                <span className="cgc-col-name" style={{ color: sc.color }}>
                  {scenarioLabel(id)}
                </span>
                <span className="cgc-col-tax">
                  {taxLabel(sc, id)}
                </span>
              </div>
            ))}
          </div>

          {/* Monthly rate row — only in Mode B */}
          {mode === "B" && (
            <div className="cgc-table-row cgc-row-highlight">
              <div className="cgc-col cgc-col-label">
                {L(lang, "Rata mensile richiesta", "Required monthly")}
              </div>
              {Object.entries(results).map(([id, r]) => (
                <div key={id} className="cgc-col cgc-row-value cgc-row-main">
                  {r.unreachable
                    ? <span className="cgc-unreachable">{L(lang, "Non raggiungibile", "Unreachable")}</span>
                    : fmt(r.monthlyRequired, sym)}
                </div>
              ))}
            </div>
          )}

          {/* Total invested */}
          <div className="cgc-table-row">
            <div className="cgc-col cgc-col-label">
              {L(lang, "Totale versato", "Total invested")}
            </div>
            {Object.entries(results).map(([id, r]) => (
              <div key={id} className="cgc-col cgc-row-value">
                {r.unreachable ? "—" : fmt(r.totalInvested, sym)}
              </div>
            ))}
          </div>

          {/* Gross final */}
          <div className="cgc-table-row">
            <div className="cgc-col cgc-col-label">
              {L(lang, "Capitale finale al lordo delle imposte", "Final capital before taxes")}
            </div>
            {Object.entries(results).map(([id, r]) => (
              <div key={id} className="cgc-col cgc-row-value">
                {r.unreachable ? "—" : fmt(r.finalGross, sym)}
              </div>
            ))}
          </div>

          {/* Tax paid */}
          <div className="cgc-table-row">
            <div className="cgc-col cgc-col-label">
              {L(lang, "Imposte pagate", "Taxes paid")}
            </div>
            {Object.entries(results).map(([id, r]) => (
              <div key={id} className="cgc-col cgc-row-value cgc-tax-cell">
                {r.unreachable ? "—" : fmt(r.totalTax, sym)}
              </div>
            ))}
          </div>

          {/* Net final (highlighted) */}
          <div className="cgc-table-row cgc-row-net">
            <div className="cgc-col cgc-col-label cgc-col-label-bold">
              {L(lang, "Capitale netto finale (nominale)", "Final net capital (nominal)")}
            </div>
            {Object.entries(results).map(([id, r]) => (
              <div key={id} className="cgc-col cgc-row-value cgc-net-value">
                {r.unreachable ? "—" : fmt(r.finalNet, sym)}
              </div>
            ))}
          </div>

          {/* Real value (only when inflation enabled) */}
          {inflationOn && (
            <div className="cgc-table-row">
              <div className="cgc-col cgc-col-label">
                {L(lang, "Valore reale (€ oggi)", "Real value (today's €)")}
              </div>
              {Object.entries(results).map(([id, r]) => (
                <div key={id} className="cgc-col cgc-row-value cgc-real-value">
                  {r.unreachable || !r.realValue ? "—" : fmt(r.realValue, sym)}
                </div>
              ))}
            </div>
          )}

          {/* vs Goal row — Mode A only, shows whether target amount is met */}
          {mode === "A" && targetAmount > 0 && (
            <div className="cgc-table-row">
              <div className="cgc-col cgc-col-label">
                {L(lang, `vs Obiettivo (${fmt(targetAmount, sym)})`, `vs Target (${fmt(targetAmount, sym)})`)}
              </div>
              {Object.entries(results).map(([id, r]) => {
                const diff = r.finalNet - targetAmount;
                return (
                  <div key={id} className={`cgc-col cgc-row-value ${diff >= 0 ? "cgc-positive" : "cgc-negative"}`}>
                    {diff >= 0 ? "+" : ""}{fmt(diff, sym)}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Shock card ───────────────────────────────────────────────── */}
        {shockCard && (
          <div className="cgc-shock-card">
            <span className="cgc-shock-icon">💡</span>
            <span className="cgc-shock-text">
              {shockCard.mode === "A"
                ? L(lang,
                    `Con le ipotesi impostate, lo scenario ${scenarioLabel(shockCard.hi)} arriva a ${fmt(shockCard.diff, sym)} netti in più dello scenario ${scenarioLabel(shockCard.lo)} in ${horizonYears} anni.`,
                    `Under the assumptions set, the ${scenarioLabel(shockCard.hi)} scenario ends ${fmt(shockCard.diff, sym)} net above the ${scenarioLabel(shockCard.lo)} scenario over ${horizonYears} years.`
                  )
                : L(lang,
                    `Con le ipotesi impostate, lo scenario ${scenarioLabel(shockCard.hi)} richiede ${fmt(shockCard.diff, sym)}/mese in meno dello scenario ${scenarioLabel(shockCard.lo)} per lo stesso obiettivo.`,
                    `Under the assumptions set, the ${scenarioLabel(shockCard.hi)} scenario needs ${fmt(shockCard.diff, sym)}/month less than the ${scenarioLabel(shockCard.lo)} scenario for the same target.`
                  )}{" "}
              {L(lang,
                "Il confronto usa rendimenti costanti e non misura il rischio: gli scenari hanno rischi molto diversi (un ETF azionario può perdere valore anche per anni). Non è un'indicazione su cosa scegliere.",
                "The comparison uses constant returns and does not measure risk: the scenarios carry very different risks (an equity ETF can lose value, even for years). It is not an indication of what to choose.")}
            </span>
          </div>
        )}
      </div>

      {/* ── Chart ──────────────────────────────────────────────────────── */}
      <div className="fire-chart-block">
        <h3 className="fire-section-title">
          {L(lang, "Crescita del Capitale (nominale)", "Capital Growth (nominal)")}
        </h3>
        <p className="fire-chart-note">
          {L(lang,
            "Saldo a fine anno al netto dei costi. Deposito e BTP: imposte già trattenute ogni anno su interessi e cedole. ETF: valore prima dell'imposta sulla plusvalenza, dovuta solo alla vendita. Rendimenti costanti, senza oscillazioni. La linea tratteggiata indica l'obiettivo.",
            "Year-end balance net of costs. Deposit and BTP: taxes already withheld every year on interest and coupons. ETF: value before the tax on the gain, due only at sale. Constant returns, no fluctuations. The dashed line marks the target."
          )}
        </p>
        <div className="cgc-chart-wrap">
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  position: "top",
                  labels: { font: { size: 12 }, boxWidth: 16, padding: 12 },
                },
                tooltip: {
                  callbacks: {
                    label: (ctx) => ` ${ctx.dataset.label}: ${fmt(ctx.parsed.y, sym)}`,
                  },
                },
              },
              scales: {
                y: {
                  ticks: { callback: v => fmt(v, sym), maxTicksLimit: 6 },
                  grid:  { color: "rgba(0,0,0,0.04)" },
                },
                x: { grid: { display: false } },
              },
            }}
          />
        </div>
      </div>

      {/* ── Disclaimer ─────────────────────────────────────────────────── */}
      <div className="fire-disclaimer">
        <p>
          {L(lang,
            "⚠️ Strumento a fini esclusivamente educativi. Ipotesi: i rendimenti lordi annui (Deposito 3%, BTP 4,2%, ETF 7%) sono valori predefiniti modificabili, costanti per tutto l'orizzonte, non medie storiche verificate né previsioni; nessuna volatilità, quindi il confronto non misura il rischio. Tassazione semplificata del regime amministrato: conto deposito 26% sugli interessi, trattenuto quando maturano; BTP a tasso fisso acquistato alla pari e tenuto a scadenza, 12,5% sulle cedole quando pagate, cedole reinvestite allo stesso tasso; ETF ad accumulazione, 26% sulla plusvalenza solo alla vendita. I costi annui includono imposta di bollo, TER e commissioni. Importi nominali: attiva il valore reale nelle impostazioni avanzate per tenere conto dell'inflazione. I rendimenti passati non sono indicativi di quelli futuri. Non costituisce consulenza in materia di investimenti né raccomandazione personalizzata.",
            "⚠️ For educational purposes only. Assumptions: gross annual returns (Deposit 3%, BTP 4.2%, ETF 7%) are editable default values, constant over the whole horizon, not verified historical averages or forecasts; no volatility, so the comparison does not measure risk. Simplified Italian administered-regime taxation: deposit account 26% on interest, withheld as it accrues; fixed-rate BTP bought at par and held to maturity, 12.5% on coupons when paid, coupons reinvested at the same rate; accumulating ETF, 26% on the gain only at sale. Annual costs include stamp duty, TER and commissions. Nominal amounts: enable real value in the advanced settings to account for inflation. Past performance does not guarantee future results. This does not constitute investment advice or a personalised recommendation."
          )}
        </p>
      </div>

      {/* ── Cross-link to KB article ────────────────────────────────────── */}
      <div className="cgc-kb-link">
        <Link to={articlePath} className="cgc-kb-link-btn">
          📚&nbsp;{L(lang,
            "Leggi l'articolo: Interesse composto e tassazione italiana →",
            "Read the article: Compound interest and Italian taxation →"
          )}
        </Link>
      </div>
    </div>
  );
}
